import React, { useRef, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { CONFIG } from 'src/consts'
import { FitText2 } from 'src/utils/string'
import { switchCurrency } from 'src/utils/number/currency'
import { toggleFullScreen } from 'src/redux/actions'
import { getFullScreenState } from 'src/redux/selectors'
import { ENTITY } from 'src/consts'
import { useAnimation } from './hooks'
import StarImg from './icon-star.svg'
import styles from './styles.module.scss'
import { cancelFullScreen } from '../../helpers'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import UpArrowIcon from 'public/assets/icons/icon-up-arrow.svg'
import DownArrowIcon from 'public/assets/icons/icon-down-arrow.svg'

export const Leaf = props => {
  const {
    group,
    entry,
    leaf,
    entity,
    baseValue,
    watchListObject,
    currency,
    isMobile,
    isWidget,
    addWatchListItems,
    deleteWatchListItems,
    dominance,
    index,
  } = props
  const fullScreen = useSelector(getFullScreenState)
  const dispatch = useDispatch()
  const router = useRouter()

  const ref = useRef(null)
  useAnimation(props, ref)

  const ChangeArrow = ({ change, size }) => {
    // Calculate the size of the arrow based on the leaf size, for example, 5% of the leaf's width
    const arrowSize = `${Math.min(size.width, size.height) * 0.1}px` // Adjust the percentage as needed

    return change > 0 ? (
      <UpArrowIcon
        className={styles.upArrow}
        style={{ width: arrowSize, height: arrowSize }}
      />
    ) : (
      <DownArrowIcon
        className={styles.downArrow}
        style={{ width: arrowSize, height: arrowSize }}
      />
    )
  }

  const cssTransform = useMemo(
    () => `translate3d(${entry.x0 - group.x0}px, ${entry.y0 - group.y0}px, 0)`,
    [entry.x0, entry.y0, group.x0, group.y0]
  )

  const changeValue = useMemo(
    () =>
      entity === ENTITY.EXCHANGE && baseValue
        ? leaf.data.changeReported
        : leaf.data.change,
    [entity, leaf, baseValue]
  )
  const changeShortValue = useMemo(
    () => (entity === ENTITY.COIN ? leaf.data.changeShort : 0),
    [entity, leaf]
  )

  // const entityPathName = useMemo(
  //   () => (entity === ENTITY.COIN ? `${leaf.data.slug}-${leaf.data.symbol.toLowerCase()}` : leaf.data.slug),
  //   [leaf, entity]
  // )

  const change = parseInt(`${Number(changeValue) * 100}`, 10) / 100
  const changeShort = parseInt(`${Number(changeShortValue) * 100}`, 10) / 100
  const width = entry.x1 - entry.x0
  const height = entry.y1 - entry.y0
  const inWatchList = watchListObject
    ? watchListObject.includes(leaf.data.slug)
    : false

  const labelSymbol = leaf.data.symbol.replace(/\*/g, '')

  const labelFontSize = useMemo(
    () =>
      FitText2({
        width,
        height: parseInt(height * 0.6),
        rows: 1,
        text: labelSymbol,
      }),
    [width, height, labelSymbol]
  )

  const changeText = `${change > 0 ? '+' : ''}${change}%`
  const changeShortText = `${changeShort > 0 ? '+' : ''}${changeShort}%`

  let starHeightWidth = (width > height ? height : width) / 8

  if (starHeightWidth > 40) {
    starHeightWidth = 40
  }

  const starStyle = {
    width: starHeightWidth,
    height: starHeightWidth,
    right: -starHeightWidth,
    top: -starHeightWidth / 3,
  }

  const stateValue =
    leaf.data[baseValue] && typeof leaf.data[baseValue] === 'number'
      ? leaf.data[baseValue].toFixed(0)
      : leaf.data[baseValue]

  const price = switchCurrency(currency, {
    price: entity === ENTITY.EXCHANGE ? stateValue : leaf.data.price,
  })

  const percentFontSize = useMemo(
    () =>
      FitText2({
        width,
        height: parseInt(height * 0.2),
        rows: 1,
        text: changeText,
        isValue: true,
      }),
    [width, height, changeText]
  )

  const priceFontSize = useMemo(
    () =>
      FitText2({
        width,
        height: parseInt(height * 0.2),
        rows: 1,
        text: price,
        maxFontSize: labelFontSize * 0.7,
      }),
    [width, height, price, labelFontSize]
  )

  const isStarEnabled = entity === ENTITY.COIN
  const isExtendedGap = entity === ENTITY.EXCHANGE

  const handleStarClick = e => {
    e.preventDefault()
    e.stopPropagation()
    inWatchList
      ? deleteWatchListItems(leaf.data.slug)
      : addWatchListItems(leaf.data.slug)
  }

  const leafContent = (
    <>
      <div
        className={styles.name}
        style={{
          fontSize: parseInt(labelFontSize),
          lineHeight: isExtendedGap ? '1.2em' : '',
        }}
      >
        {isMobile || !isStarEnabled || isWidget ? null : (
          <StarImg
            className={clsx(styles.star, { [styles.active]: inWatchList })}
            style={starStyle}
            viewBox={'0 0 39 37'}
            onClick={handleStarClick}
          />
        )}
        {labelSymbol}
      </div>
      <div
        className={styles.price}
        style={{
          fontSize: parseInt(priceFontSize),
          animationDuration: `${CONFIG.AUTO_UPDATE_LEAF_ANIMATION_DURATION}s`,
        }}
      >
        {price}
      </div>
      <div
        className={styles.change}
        style={{
          fontSize: parseInt(percentFontSize * 0.45),
          marginTop: parseInt(percentFontSize * 0.14),
          animationDuration: `${CONFIG.AUTO_UPDATE_LEAF_ANIMATION_DURATION}s`,
        }}
      >
        <ChangeArrow change={change} size={{ width, height }} /> {changeText}
      </div>
      {/* TODO entity === ENTITY.COIN && (
        <div
          className={styles.change}
          style={{
            fontSize: parseInt(percentFontSize * 0.35),
            marginTop: parseInt(percentFontSize * 0.14),
            animationDuration: `${CONFIG.AUTO_UPDATE_LEAF_ANIMATION_DURATION}s`,
            color: '#2e2d2f',
          }}
        >
          {changeShortText}
        </div>
      )*/}
      {dominance && (
        <div
          className={styles.dominance}
          style={{
            fontSize: percentFontSize * (isMobile ? 0.225 : 0.17),
            bottom: percentFontSize * 0.16,
          }}
        >
          Dominance: {dominance}
        </div>
      )}
    </>
  )

  const wrapperProps = {
    className: styles.leaf,
    'data-index': index,
    'data-t': 'leaf',
    'data-symbol': leaf.data.symbol,
    'data-name': 'name',
    style: {
      transform: cssTransform,
      width,
      height,
      willChange: 'transform',
      animationDuration: `${CONFIG.AUTO_UPDATE_LEAF_ANIMATION_DURATION}s`,
    },
  }

  const href = `/${entity}/${leaf.data.slug}`

  const handleClick = event => {
    event.preventDefault()
    event.stopPropagation()

    if (fullScreen) {
      cancelFullScreen()
      dispatch(toggleFullScreen(false))
    }
    router.push(event.currentTarget.href)
  }

  if (!isWidget) {
    // using a instead of next Link to disable prefeching on hover too
    return (
      <a href={href} onClick={handleClick}>
        <div ref={ref} {...wrapperProps}>
          {leafContent}
        </div>
      </a>
    )
  }

  return (
    <a href={href} {...wrapperProps} target='_blank' rel='noopener noreferrer'>
      {leafContent}
    </a>
  )
}
